import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { checkPassword, checkUserName } from "../../../redux/actions/auth/auth";
import { CHECK_PASSWORD, CHECK_USERNAME } from "../../../redux/actionTypes/actionsTypes";
import { CheckPasswordState } from "../../../redux/reducers/getAuthReducer/checkPasswordReducer";
import { CheckUsernameState } from "../../../redux/reducers/getAuthReducer/checkUsernameReducer";
import { RootState } from "../../../redux/reducers/root";
import AmpArrowTooltip from "../../common/AmpArrowTooltip/AmpArrowTooltip";
import AmpButton from "../../common/AmpButton/AmpButton";
import AmpCheckBox from "../../common/AmpCheckBox/AmpCheckBox";
import AmpInlineLoadingSpinner from "../../common/AmpInlineLoadingSpinner/AmpInlineLoadingSpinner";
import AmpInput from "../../common/AmpInput/AmpInput";
import SmallHeading from "../../common/AmpSmallHeading/AppSmallHeading";
import PasswordValidator from "../../login/PasswordValidator/PasswordValidator";
import { FormRegTabs } from "../RegistrationTypes";

interface EmailPwdProps extends FormRegTabs {
    setEmailPwdFormValid: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function EmailPwd(props: EmailPwdProps) {

    const { t } = useTranslation('login');
    const dispatch = useDispatch();

    // Email State
    const [email, setEmail] = useState("");
    const [confEmail, setConfEmail] = useState("");

    // Password State
    const [password, setPassword] = useState("");
    const [confPassword, setConfPassword] = useState("");
    const [showPassword, setShowPassword] = useState<"text" | "password">("password");

    // Email Error State
    const [emailValid, setEmailValid] = useState(false);
    const [confEmailValid, setConfEmailValid] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState<string | undefined>(undefined);
    const [confEmailErrorMessage, setConfEmailErrorMessage] = useState<string | undefined>(undefined);

    // Password Error State
    const [passwordValid, setPasswordValid] = useState(false);
    const [confPasswordValid, setConfPasswordValid] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<string | undefined>(undefined);
    const [confPasswordErrorMessage, setConfPasswordErrorMessage] = useState<string | undefined>(undefined);

    // Redux Stores 
    // Check password/username
    const usernameCheckResult = useSelector((state: RootState) => state.authReducer.usernameCheck as CheckUsernameState);
    const passwordCheckResult = useSelector((state: RootState) => state.authReducer.passwordCheck as CheckPasswordState);

    // AODA label ids / fors
    const emailId = "reg-email";
    const emailConfId = "reg-conf-email";
    const pwdId = "reg-password";
    const confPwdId = "reg-conf-password";

    const validateEmail = (val: string) => {
        if (!val.match(/^[a-zA-Z0-9_.%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
            return false;
        }
        return true;
    }

    const checkUserNameValid = (username: string) => {
        if (username !== email) {
            setEmail(username);
            const emailIsValid = validateEmail(username)
            setEmailValid(emailIsValid);
            setEmailErrorMessage((emailIsValid || username === "") ? undefined : t('2-email-invalid-error') as string);
        }
    }

    const checkConfEmail = (username: string) => {
        if (username !== email && confEmail !== "" && username !== "") {
            setConfEmailValid(false);
            setConfEmailErrorMessage(t('2-conf-email-invalid') as string);
        } else {
            setConfEmailValid(true);
            setConfEmailErrorMessage(undefined);
        }
        setConfEmail(username);
    }

    const validatePassword = (val: string) => {
        const regex = /^(?=.*\d)(?=.*[A-Z])(?=.*[a-z])(?=.*[^a-zA-Z0-9])([^\s]){11,}$/
        return regex.test(val);
    }

    const checkPasswordValid = (pwd: string) => {
        if (password !== pwd) {
            setPassword(pwd);
            const passwordIsValid = validatePassword(pwd);
            setPasswordErrorMessage(passwordIsValid || pwd === "" ? undefined : t('2-password-not-valid') as string);
            setPasswordValid(passwordIsValid);
        }
    }

    const checkConfPassword = (pwd: string) => {
        if (password !== pwd && confPassword !== "" && pwd !== "") {
            setConfPasswordValid(false);
            setConfPasswordErrorMessage(t('2-conf-pwd-not-match') as string);
        } else {
            setConfPasswordValid(true);
            setConfPasswordErrorMessage(undefined);
        }
        setConfPassword(pwd);
    }

    const displayPassword = (e: React.ChangeEvent) => {
        setShowPassword((e.currentTarget as HTMLInputElement).checked ? "text" : "password");
    }

    const validateEmailPasswordForm = () => {
        console.log("did something");
        dispatch(checkPassword(password));
        dispatch(checkUserName(email));
    }


    // Password and Email Checks
    useEffect(() => {
        if (!usernameCheckResult.loading && !passwordCheckResult.loading) {

            let emailIsValid = false;
            let passwordIsValid = false;
            // Determine if username is valid
            const usernameResult = usernameCheckResult.username;
            const passwordResult = passwordCheckResult.password;

            if (usernameResult.user_id === email) {
                if (usernameResult.valid && !usernameResult.exists) {
                    setEmailValid(true);
                    emailIsValid = true;
                } else if (usernameResult.exists) {
                    setEmailValid(false);
                    setEmailErrorMessage(t('2-email-taken-error') as string);
                } else if (usernameResult.valid === false) {
                    setEmailValid(false);
                    setEmailErrorMessage(t('2-email-invalid-error') as string);
                }
            }

            // Determine if password is valid
            if (passwordResult.pwd === password) {
                if (passwordResult.valid) {
                    setPasswordValid(true);
                    passwordIsValid = true;
                } else if (!passwordResult.valid && password !== "") {
                    setPasswordValid(false);
                    setPasswordErrorMessage(t('2-password-not-valid') as string);
                }
            }

            const formIsValid = emailIsValid &&
                confEmailValid &&
                confEmail !== "" &&
                confEmail === email &&
                passwordIsValid &&
                confPasswordValid &&
                confPassword !== "" &&
                confPassword === password

            if (formIsValid) {
                const payload = {...props.payload}
                payload.username = email;
                payload.password = password;
                props.setEmailPwdFormValid(formIsValid);
                props.setPayload(payload);
                props.setActiveTab(3);
                dispatch({type: CHECK_USERNAME.RESET});
                dispatch({type: CHECK_PASSWORD.RESET});
            }

        }
    // eslint-disable-next-line
    }, [usernameCheckResult, passwordCheckResult]);


    return <>
        <div className="dark-blue-background">
            {t('2-top-text') as string}
        </div>
        <div className="email-password-form">
            <AmpArrowTooltip title={t('password-tooltip') as string} items={[]} element={<PasswordValidator password={password} />} className={(emailErrorMessage && confEmailErrorMessage) ? "move-down-two" : (emailErrorMessage || confEmailErrorMessage) ? "move-down-one" : undefined} />
            <div className="login-container-mid">
                <SmallHeading for={emailId}>
                    {t('2-email') as string}
                </SmallHeading>
                <AmpInput noTimer={true} onInput={checkUserNameValid} type="text" id={emailId} name="username" autoComplete="off" errorMessage={emailErrorMessage} />

                <SmallHeading for={emailConfId}>
                    {t('2-conf-email') as string}
                </SmallHeading>
                <AmpInput noTimer={true} onInput={checkConfEmail} type="text" id={emailConfId} name="conf-email" disabled={!emailValid} autoComplete="off" errorMessage={confEmailErrorMessage} />

                <SmallHeading for={pwdId}>
                    {t('2-pwd') as string}
                </SmallHeading>
                <AmpInput id={pwdId} name="password" type={showPassword} onInput={checkPasswordValid} noTimer={true} autoComplete="new-password" errorMessage={passwordErrorMessage} />

                <SmallHeading for={confPwdId}>
                    {t('2-conf-pwd') as string}
                </SmallHeading>
                <AmpInput noTimer={true} onInput={checkConfPassword} type={showPassword} id={confPwdId} name="conf-password" disabled={!passwordValid} autoComplete="new-password" errorMessage={confPasswordErrorMessage} />
                <div className="show-password-checkbox">
                    <AmpCheckBox isSlider={true} id="showPassword" label={t("show-password") as string} hasToolTip={false} toolTip="" handleCheck={displayPassword} />
                </div>
            </div>

            <div className="login-container-footer">
                <Link rel="noreferrer noopener" target="_blank" className="amp-anchor" to="/auth/contact-us">{t("need-help") as string}</Link>
                <div>
                    <AmpButton type="cancel" onClick={() => props.triggerCancel(true)} text={t("cancel")} />
                    <AmpButton type="submit" onClick={() => validateEmailPasswordForm()} text={(usernameCheckResult.loading || passwordCheckResult.loading) ? <AmpInlineLoadingSpinner /> : t("next")} disabled={usernameCheckResult.loading || passwordCheckResult.loading || !emailValid || !confEmailValid || !passwordValid || !confPasswordValid} />
                </div>
            </div>
        </div>
    </>
}
